import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import getUuidByString from 'uuid-by-string';
import { PAY_WITH_CASH, isFeatureEnabled } from 'utilities/features';
import PageLoader from 'typescript/components/page_loader';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { getCountryCode, getLocaleString } from 'typescript/utilities/locales';
import {
  initCashAppPay,
  cashAppPayParams,
  createPaymentRequestPayload,
  paymentSdkAvailable,
  usePaymentSdk,
} from 'typescript/utilities/web_payment_sdk';
import { useGetOrderSummary } from 'typescript/hooks/use_order_actions_client';
import { CHECKOUT_ROUTES } from 'typescript/constants/enums/checkout';
import { useSubmitOrder } from 'typescript/apps/checkout_v3/components/payment/use_submit_order';
import { PaymentType } from 'typescript/constants/enums/payment';
import { useCompleteOrderContext } from 'typescript/context/complete_order_context';
import RecaptchaModal from 'typescript/apps/checkout_v3/components/recaptcha/recaptcha_modal';

const CashAppRedirect = () => {
  const history = useHistory();
  const { scriptLoaded, loadFailed } = usePaymentSdk();
  const { completeOrderError, showRecaptcha } = useCompleteOrderContext();
  const { orderNumber, applicationId, locationId } = useCheckoutFlow();

  const {
    orderSummaryData,
    isPending: orderDataIsPending,
    error: orderDataError,
  } = useGetOrderSummary({ orderNumber });

  const {
    submitOrder,
    completeOrderAfterRecaptcha,
    cancelPendingCompleteOrder,
  } = useSubmitOrder(orderNumber);

  const handleRecaptchaCancelled = () => {
    cancelPendingCompleteOrder();
    history.push(CHECKOUT_ROUTES.PAYMENT, { error: 'cash_app_pay' });
  };

  if (orderDataError) {
    history.push(CHECKOUT_ROUTES.DELIVERY);
  }

  if (orderDataIsPending) {
    return <PageLoader />;
  }

  const validateReferenceId = (referenceId: string): boolean => {
    const uuid = getUuidByString(orderNumber + orderSummaryData.total.amount);
    return uuid === referenceId;
  };

  useEffect(() => {
    if (orderSummaryData) {
      if (
        !isFeatureEnabled(PAY_WITH_CASH) ||
        orderSummaryData.payment_due.payment_schedule === 'installment'
      ) {
        history.push(CHECKOUT_ROUTES.DELIVERY);
      }
    }
  }, [orderSummaryData]);

  useEffect(() => {
    if (!orderSummaryData || !scriptLoaded) return;

    // scriptLoaded implies the global existed moments ago; if it has gone
    // missing anyway, bail to the payment page's error state rather than
    // throwing inside this effect and blanking the spinner-only page.
    if (!paymentSdkAvailable()) {
      history.push(CHECKOUT_ROUTES.PAYMENT, { error: 'cash_app_pay' });
      return;
    }

    const payments = window.Square.payments(applicationId, locationId);
    payments.setLocale(getLocaleString());

    initCashAppPay(
      payments,
      createPaymentRequestPayload(orderSummaryData, getCountryCode()),
      cashAppPayParams(window.location.pathname, 'pass'),
    ).then((cashAppPay) => {
      if (!cashAppPay) {
        // TODO (bdang) : Migrate exisitng utils to redirect while maintining params
        history.push(CHECKOUT_ROUTES.DELIVERY, { error: 'cash_app_pay' });
        return;
      }

      cashAppPay.addEventListener('ontokenization', (event: CustomEvent) => {
        const { tokenResult } = event.detail;
        const referenceId = tokenResult?.details?.cashAppPay?.referenceId;

        if (tokenResult && validateReferenceId(referenceId)) {
          submitOrder({
            paymentMethod: cashAppPay,
            paymentType: PaymentType.CASH_APP_PAY,
            sqPayments: payments,
            cashAppTokenResult: tokenResult,
            // Consent was enforced before the redirect (the Cash App button
            // only attaches once the checkbox is checked); this fresh page has
            // lost the in-memory flag.
            subscriptionConsentPreVerified: true,
          });
        } else {
          history.push(CHECKOUT_ROUTES.DELIVERY, { error: 'cash_app_pay' });
        }
      });
    });
  }, [orderSummaryData, scriptLoaded]);

  // Redirect to payment page if there is an error in completing order.
  useEffect(() => {
    if (completeOrderError) {
      history.push(CHECKOUT_ROUTES.PAYMENT, { error: 'cash_app_pay' });
    }
  }, [completeOrderError]);

  // Without the SDK this page can only spin forever; send the buyer back to
  // the payment page, which surfaces the cash_app_pay error state.
  useEffect(() => {
    if (loadFailed) {
      history.push(CHECKOUT_ROUTES.PAYMENT, { error: 'cash_app_pay' });
    }
  }, [loadFailed]);

  return (
    <>
      {showRecaptcha && (
        <RecaptchaModal
          onRecaptchaVerified={completeOrderAfterRecaptcha}
          onRecaptchaCancelled={handleRecaptchaCancelled}
        />
      )}
      <PageLoader />
    </>
  );
};

export default CashAppRedirect;
